import type { DismissGuard } from '../hooks/useDismissGuard'
import { ModalShell } from './ModalShell'

interface UnsavedChangesModalProps {
  open: boolean
  isSaving?: boolean
  onClose: () => void
  onDiscard: () => void
  /** Omitted while the changes can't be saved as they stand: Cancel and Discard only. */
  onSave?: () => void
  message?: string
  saveLabel?: string
  discardLabel?: string
  cancelLabel?: string
  /** Above a surface that sits over the page's own layers. */
  zIndex?: number
}

export default function UnsavedChangesModal({
  open,
  isSaving = false,
  onClose,
  onDiscard,
  onSave,
  message = 'Would you like to save your changes before switching views?',
  saveLabel = 'Save',
  discardLabel = 'Discard',
  cancelLabel = 'Cancel',
  zIndex,
}: UnsavedChangesModalProps) {
  return (
    <ModalShell
      title="Unsaved Changes"
      open={open}
      onClose={() => {
        if (!isSaving) onClose()
      }}
      width="md"
      className="left-1/2 top-1/3 -translate-x-1/2"
      style={zIndex === undefined ? undefined : { zIndex }}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="rounded-md px-3 py-1.5 text-xs font-medium text-muted transition-colors hover:bg-muted hover:text-default"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onDiscard}
            disabled={isSaving}
            className="rounded-md border border-default surface-default px-3 py-1.5 text-xs font-medium text-default transition-colors hover:bg-muted"
          >
            {discardLabel}
          </button>
          {onSave ? (
            <button
              type="button"
              onClick={onSave}
              disabled={isSaving}
              className="rounded-md bg-[color:var(--action-brand-bg)] px-3 py-1.5 text-xs font-medium text-[color:var(--action-brand-text)] transition-colors hover:bg-[color:var(--action-brand-bg-hover)]"
            >
              {isSaving ? 'Saving...' : saveLabel}
            </button>
          ) : null}
        </div>
      }
    >
      <p className="text-sm text-default">{message}</p>
    </ModalShell>
  )
}

interface UnsavedChangesQuestionProps {
  guard: DismissGuard
  /** What would be lost, such as "Your filter changes haven't been applied." */
  message: string
  /** The surface's own word for keeping the edits, such as Apply. */
  saveLabel: string
  /** Keeps the edits as that button does; null while they can't be kept as they stand. */
  onSave: (() => void) | null
  zIndex?: number
}

/**
 * The question a dialog or panel asks (see useDismissGuard) when a click
 * outside, Escape or its close button would throw its edits away.
 */
export function UnsavedChangesQuestion({
  guard,
  message,
  saveLabel,
  onSave,
  zIndex,
}: UnsavedChangesQuestionProps) {
  return (
    <UnsavedChangesModal
      open={guard.isAsking}
      message={message}
      cancelLabel="Keep editing"
      saveLabel={saveLabel}
      zIndex={zIndex}
      onClose={guard.keepEditing}
      onDiscard={guard.discard}
      onSave={onSave ? () => guard.save(onSave) : undefined}
    />
  )
}
